/**
 * Shared Living. Doc: docs/overall-plan/02-MANGO-PERSONAL/shared-living
 * Wired to GET/POST /api/v1/shared-living.
 */
import React, { useEffect, useState } from 'react';
import { sharedLivingApi, type SharedLivingGroup } from '../services/api';

const SharedLiving: React.FC = () => {
  const [households, setHouseholds] = useState<SharedLivingGroup[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [addBillGroupId, setAddBillGroupId] = useState<string | null>(null);
  const [addChoreGroupId, setAddChoreGroupId] = useState<string | null>(null);
  const [addExpenseGroupId, setAddExpenseGroupId] = useState<string | null>(null);
  const [billTitle, setBillTitle] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [billDueDate, setBillDueDate] = useState('');
  const [choreTitle, setChoreTitle] = useState('');
  const [choreAssignedTo, setChoreAssignedTo] = useState('');
  const [choreDueDate, setChoreDueDate] = useState('');
  const [expenseTitle, setExpenseTitle] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expensePaidBy, setExpensePaidBy] = useState('');
  const [submittingItem, setSubmittingItem] = useState(false);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await sharedLivingApi.getHouseholds();
      if (res?.success) {
        setHouseholds(Array.isArray(res?.data) ? res.data : []);
        setMessage(res?.message ?? null);
      } else setError(res?.error || 'Failed to load');
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await sharedLivingApi.createGroup({
        name: newName.trim() || 'Household',
        description: newDesc.trim() || undefined,
      });
      if (res?.success) {
        setNewName('');
        setNewDesc('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add household');
    } catch {
      setError('Failed to add household');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBill = async (groupId: string) => {
    const amt = parseFloat(billAmount);
    if (!billTitle.trim() || isNaN(amt) || amt < 0) return;
    setSubmittingItem(true);
    setError(null);
    try {
      const res = await sharedLivingApi.addBill(groupId, {
        title: billTitle.trim(),
        amount: amt,
        dueDate: billDueDate || undefined,
      });
      if (res?.success) {
        setBillTitle('');
        setBillAmount('');
        setBillDueDate('');
        setAddBillGroupId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to add bill');
    } catch {
      setError('Failed to add bill');
    } finally {
      setSubmittingItem(false);
    }
  };

  const handleAddChore = async (groupId: string) => {
    if (!choreTitle.trim()) return;
    setSubmittingItem(true);
    setError(null);
    try {
      const res = await sharedLivingApi.addChore(groupId, {
        title: choreTitle.trim(),
        assignedTo: choreAssignedTo.trim() || undefined,
        dueDate: choreDueDate || undefined,
      });
      if (res?.success) {
        setChoreTitle('');
        setChoreAssignedTo('');
        setChoreDueDate('');
        setAddChoreGroupId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to add chore');
    } catch {
      setError('Failed to add chore');
    } finally {
      setSubmittingItem(false);
    }
  };

  const handleAddExpense = async (groupId: string) => {
    const amt = parseFloat(expenseAmount);
    if (!expenseTitle.trim() || isNaN(amt) || amt < 0) return;
    setSubmittingItem(true);
    setError(null);
    try {
      const res = await sharedLivingApi.addExpense(groupId, {
        title: expenseTitle.trim(),
        amount: amt,
        paidBy: expensePaidBy.trim() || undefined,
      });
      if (res?.success) {
        setExpenseTitle('');
        setExpenseAmount('');
        setExpensePaidBy('');
        setAddExpenseGroupId(null);
        loadData(true);
      } else setError(res?.error || 'Failed to add expense');
    } catch {
      setError('Failed to add expense');
    } finally {
      setSubmittingItem(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>🏠 Shared Living</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Manage shared households, bills, chores and expenses
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '8px', color: '#f5576c', cursor: 'pointer', fontSize: '14px' }}
            >
              Add Household
            </button>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
          <input
            type="text"
            placeholder="Household name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleAdd} disabled={submitting} style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              {submitting ? 'Adding…' : 'Create'}
            </button>
            <button type="button" onClick={() => { setShowAdd(false); setNewName(''); setNewDesc(''); }} style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}
      {message && !error && households.length === 0 && <p style={{ marginBottom: '16px', fontSize: '14px', color: '#64748b' }}>{message}</p>}
      {households.length === 0 && !error && !showAdd && (
        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
          No households yet. Add a household above to start splitting bills and chores.
        </div>
      )}
      {households.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {households.map((group) => {
            const billCount = group.bills?.length ?? 0;
            const choreCount = group.chores?.length ?? 0;
            const expenseCount = group.expenses?.length ?? 0;
            const expanded = expandedGroupId === group.id;
            const showBillForm = addBillGroupId === group.id;
            const showChoreForm = addChoreGroupId === group.id;
            const showExpenseForm = addExpenseGroupId === group.id;
            return (
              <li
                key={group.id}
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #e2e8f0',
                  background: '#fafafa',
                  marginBottom: '8px',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                  onClick={() => setExpandedGroupId(expanded ? null : group.id)}
                >
                  <div>
                    <strong>{group.name}</strong>
                    {group.description && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>{group.description}</p>
                    )}
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                      {billCount} bill{billCount !== 1 ? 's' : ''} · {choreCount} chore{choreCount !== 1 ? 's' : ''} · {expenseCount} expense{expenseCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>{expanded ? '−' : '+'}</span>
                </div>
                {expanded && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                    {/* Bills */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>Bills</strong>
                        {!showBillForm && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setAddBillGroupId(group.id); }} style={{ padding: '4px 10px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+ Add</button>
                        )}
                      </div>
                      {group.bills && group.bills.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}>
                          {group.bills.map((b) => (
                            <li key={b.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{b.title} - ${b.amount}</li>
                          ))}
                        </ul>
                      )}
                      {showBillForm && (
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
                          <input type="text" placeholder="Bill title" value={billTitle} onChange={(e) => setBillTitle(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="number" placeholder="Amount" value={billAmount} onChange={(e) => setBillAmount(e.target.value)} min="0" step="0.01" style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="date" placeholder="Due date" value={billDueDate} onChange={(e) => setBillDueDate(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => handleAddBill(group.id)} disabled={submittingItem || !billTitle.trim() || !billAmount} style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>{submittingItem ? 'Adding...' : 'Add'}</button>
                            <button type="button" onClick={() => { setAddBillGroupId(null); setBillTitle(''); setBillAmount(''); setBillDueDate(''); }} style={{ padding: '6px 12px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Chores */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>Chores</strong>
                        {!showChoreForm && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setAddChoreGroupId(group.id); }} style={{ padding: '4px 10px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+ Add</button>
                        )}
                      </div>
                      {group.chores && group.chores.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}>
                          {group.chores.map((c) => (
                            <li key={c.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{c.title}</li>
                          ))}
                        </ul>
                      )}
                      {showChoreForm && (
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
                          <input type="text" placeholder="Chore title" value={choreTitle} onChange={(e) => setChoreTitle(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="text" placeholder="Assigned to (optional)" value={choreAssignedTo} onChange={(e) => setChoreAssignedTo(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="date" placeholder="Due date" value={choreDueDate} onChange={(e) => setChoreDueDate(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => handleAddChore(group.id)} disabled={submittingItem || !choreTitle.trim()} style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>{submittingItem ? 'Adding...' : 'Add'}</button>
                            <button type="button" onClick={() => { setAddChoreGroupId(null); setChoreTitle(''); setChoreAssignedTo(''); setChoreDueDate(''); }} style={{ padding: '6px 12px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Expenses */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>Expenses</strong>
                        {!showExpenseForm && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setAddExpenseGroupId(group.id); }} style={{ padding: '4px 10px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+ Add</button>
                        )}
                      </div>
                      {group.expenses && group.expenses.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '8px' }}>
                          {group.expenses.map((exp) => (
                            <li key={exp.id} style={{ padding: '6px 0', fontSize: '13px', borderBottom: '1px solid #e2e8f0' }}>{exp.title} - ${exp.amount}</li>
                          ))}
                        </ul>
                      )}
                      {showExpenseForm && (
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
                          <input type="text" placeholder="Expense title" value={expenseTitle} onChange={(e) => setExpenseTitle(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="number" placeholder="Amount" value={expenseAmount} onChange={(e) => setExpenseAmount(e.target.value)} min="0" step="0.01" style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <input type="text" placeholder="Paid by (optional)" value={expensePaidBy} onChange={(e) => setExpensePaidBy(e.target.value)} style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => handleAddExpense(group.id)} disabled={submittingItem || !expenseTitle.trim() || !expenseAmount} style={{ padding: '6px 12px', background: '#16a34a', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>{submittingItem ? 'Adding...' : 'Add'}</button>
                            <button type="button" onClick={() => { setAddExpenseGroupId(null); setExpenseTitle(''); setExpenseAmount(''); setExpensePaidBy(''); }} style={{ padding: '6px 12px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SharedLiving;
